import type { FieldConfig } from '@/components/TimesheetReports/FieldSelector';
import { CheckCircle2, XCircle, Clock, RotateCcw, FileText, Edit3, Share2 } from 'lucide-react';
import type { AuditStatus, WorkflowEventType, WorkflowStage } from '@/utils/AuditTrail/types';
import type { ElementType } from 'react';

// ─── Status configuration ─────────────────────────────────────────────────────

export const STATUS_CONFIG: Record<AuditStatus, { label: string; className: string; icon: ElementType }> = {
  approved: {
    label: 'Approved',
    className: 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200',
    icon: CheckCircle2,
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-200',
    icon: XCircle,
  },
  recalled: {
    label: 'Recalled',
    className: 'bg-burntorange-100 text-burntorange-800 dark:bg-burntorange-900 dark:text-burntorange-200',
    icon: RotateCcw,
  },
  submitted: {
    label: 'Submitted',
    className: 'bg-info-100 text-info-800 dark:bg-info-900 dark:text-info-200',
    icon: Clock,
  },
};

export const EVENT_CONFIG: Record<WorkflowEventType, { label: string; className: string; icon: ElementType }> = {
  submitted: {
    label: 'Submitted',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    icon: FileText,
  },
  recalled: {
    label: 'Recalled',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    icon: RotateCcw,
  },
  resubmitted: {
    label: 'Resubmitted',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    icon: FileText,
  },
  approved: {
    label: 'Approved',
    className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    icon: CheckCircle2,
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    icon: XCircle,
  },
  delegated: {
    label: 'Delegated',
    className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    icon: Share2,
  },
  approver_edited: {
    label: 'Approver Edited',
    className: 'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200',
    icon: Edit3,
  },
};

export const STAGE_STATUS_CONFIG: Record<WorkflowStage['status'], { label: string; className: string }> = {
  approved: { label: 'Approved', className: 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200' },
  rejected: { label: 'Rejected', className: 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-200' },
  pending:  { label: 'Pending',  className: 'bg-info-100 text-info-800 dark:bg-info-900 dark:text-info-200' },
  skipped:  { label: 'Skipped',  className: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' },
};

export const APPROVAL_TYPE_LABELS: Record<string, string> = {
  any: 'Any One Approver',
  all: 'All Approvers',
  majority: 'Majority',
  sequential: 'Sequential',
};

export const AUDIT_DEFAULT_COLUMNS: FieldConfig[] = [
  { id: 'employeeName', label: 'Employee Name', visible: true, order: 0, category: 'People' },
  { id: 'weekEnding',   label: 'Week Ending',   visible: true, order: 1, category: 'Time Period' },
  { id: 'department',   label: 'Department',    visible: true, order: 2, category: 'People' },
  { id: 'status',       label: 'Status',        visible: true, order: 3, category: 'Status' },
  { id: 'lastEvent',    label: 'Last Event',    visible: true, order: 4, category: 'Details' },
  { id: 'totalHours',   label: 'Total Hours',   visible: true, order: 5, category: 'Hours' },
];

export const PAGE_SIZE = 25;
